import {action, computed, observable, runInAction} from 'mobx';

import {STATE_PAUSED, STATE_PLAYING, STATE_STOPPED} from '../../constants/displayItemConstants';

/**
 * The request animation frame function.
 * @type {function}
 */
const safeRequestAnimationFrame = window.requestAnimationFrame || window.webkitRequestAnimationFrame;

/**
 * Recursively calls the request animation frame until the number of iterations has been reached.
 *
 * @param {number} remaining
 * @param {function} callback
 * @returns {*}
 */
function requestRecursion(remaining, callback) {
  if (remaining <= 1) {
    return safeRequestAnimationFrame(callback);
  }

  return safeRequestAnimationFrame(() => {
    return requestRecursion(remaining - 1, callback);
  });
}

/**
 * Converts seconds to milliseconds when multiplied.
 * @const {number}
 */
const TO_MILLISECONDS = 1000;

/**
 * The default fps.
 * This must be 60, 30, 20, 15, 12, 10.
 * (60 / N) where N >= 1
 *
 * @type {number}
 */
const DEFAULT_FPS = 60;

/**
 * The game timer store.
 *
 * @returns {{}}
 */
export class GameTimerStore {
  /**
   * The handler to call on each 'tick'.
   *
   * @type {?function}
   */
  @observable tickHandler = null;

  /**
   * The maximum amount of time (in milliseconds) that can elapse before the timer stops itself.
   *
   * @type {?number}
   */
  @observable maxTime = null;

  /**
   * Whether or not the timer is paused.
   *
   * @type {boolean}
   */
  @observable isPaused = false;

  /**
   * The DOMHighResTimeStamp of when the requestanimationframe (RAF) loop started.
   *
   * @type {number}
   */
  @observable startTime = 0;

  /**
   * The time (in milliseconds) that the timer has been running.
   *
   * @type {number}
   */
  @observable elapsedTime = 0;

  /**
   * The id of the last requestanimationframe (RAF) request.
   *
   * @type {?number}
   */
  @observable rafRequestId = null;

  /**
   * Whether or not the timer continues to fire even when paused.
   *
   * @type {boolean}
   */
  @observable isContinuous = false;

  /**
   * The timer fps.
   * This must be in this list: [60, 30, 20, 15, 12, 10].
   *
   * @type {number}
   */
  @observable fps = DEFAULT_FPS;

  /**
   * Returns whether or not the timer is running.
   * Started means the timer is greater than zero, it would be stopped only if the time is zero.
   * The timer can be started and paused at the same time.
   *
   * @returns {boolean}
   */
  @computed get isStarted() {
    return (this.elapsedTime > 0);
  }

  /**
   * Gets the state of the timer (stopped, paused, playing).
   *
   * @returns {string}
   */
  @computed get state() {
    if (this.isPaused) {
      return STATE_PAUSED;
    } else if (this.isStarted) {
      return STATE_PLAYING;
    }
    return STATE_STOPPED;
  }

  /**
   * The current timer time.
   *
   * @returns {number}
   */
  @computed get time() {
    return this.elapsedTime;
  }

  /**
   * Requests the next animation frame based on the timer fps.
   *
   * @param {function} callback
   * @returns {number} The request animation frame request id.
   */
  requestFrame(callback) {
    const {fps} = this;

    const maxFps = 60;
    const rounds = Math.floor(maxFps / fps);

    return requestRecursion(rounds, callback);
  }

  /**
   * Fires the tickHandler if it is defined.
   *
   * @param {{}} eventData
   */
  fireTickHandler(eventData) {
    if (!this.tickHandler) {
      return;
    }

    this.tickHandler(Object.assign({
      isPaused: this.isPaused,
      time: this.elapsedTime
    }, eventData || {}));
  }

  /**
   * Initializes the stepping process.
   * This method is designed to be used by phantomJS, not the normal display.
   *
   * @returns {boolean}
   */
  @action initSteps() {
    if (this.elapsedTime > 0) {
      return false;
    }

    this.elapsedTime = 0;
    this.fireTickHandler({
      forceTransition: true,
    });
    return true;
  }

  /**
   * Steps through 1/60th of the game loop every time this is called.
   * This method is designed to be used by phantomJS, not the normal display.
   *
   * @param {number} time
   * @param {boolean=} setToTime
   * @returns {boolean}
   */
  @action step(time, setToTime) {
    if (typeof time === 'number') {
      if (setToTime) {
        this.elapsedTime = time;
      } else {
        this.elapsedTime += time;
      }
    } else {
      this.elapsedTime += ((1 / this.fps) * TO_MILLISECONDS);
    }

    if (this.elapsedTime > this.maxTime) {
      this.elapsedTime = this.maxTime;
    }

    this.fireTickHandler({
      forceTransition: true,
    });
    return true;
  }

  /**
   * Starts the timer and game loop.
   */
  @action start() {
    let unpause = Boolean(this.isPaused);
    if (this.maxTime !== null && this.elapsedTime >= this.maxTime) {
      this.startTime = 0;
      this.elapsedTime = 0;
    }

    const self = this; // eslint-disable-line no-shadow

    /**
     * The game time loop.
     *
     * @param {number} currentTime
     */
    function timerLoop(currentTime) {
      runInAction('timerLoop', () => {
        if (self.isPaused && !unpause) {
          if (!self.isContinuous) {
            return;
          }
          self.fireTickHandler();
          self.rafRequestId = self.requestFrame(timerLoop);
          return;
        }

        if (self.isPaused) {
          self.isPaused = false;
          unpause = false;
          self.startTime = currentTime - self.elapsedTime;
        } else if (!self.startTime) {
          self.startTime = currentTime;
          self.elapsedTime = 0;
        } else {
          self.elapsedTime = currentTime - self.startTime;
        }

        // Check for the maximum time.
        if (self.maxTime !== null && self.elapsedTime >= self.maxTime) {
          self.elapsedTime = self.maxTime;
          self.pause();
          return;
        }

        self.fireTickHandler();
        self.rafRequestId = self.requestFrame(timerLoop);
      });
    }

    self.fireTickHandler({
      forceTransition: true,
    });
    this.rafRequestId = this.requestFrame(timerLoop);
  }

  /**
   * Pauses the timer.
   */
  @action pause() {
    this.isPaused = true;
    if (this.rafRequestId && !this.isContinuous) {
      cancelAnimationFrame(this.rafRequestId);
      this.rafRequestId = null;
    }
    this.fireTickHandler();
  }

  /**
   * Stops the timer.
   */
  @action stop() {
    if (this.rafRequestId && !this.isContinuous) {
      cancelAnimationFrame(this.rafRequestId);
      this.rafRequestId = null;
    }
    this.isPaused = Boolean(this.isContinuous);

    this.fireTickHandler();
  }

  /**
   * Stops the timer and resets the time to zero.
   */
  @action stopAndReset() {
    if (this.rafRequestId && !this.isContinuous) {
      cancelAnimationFrame(this.rafRequestId);
      this.rafRequestId = null;
    }
    this.isPaused = Boolean(this.isContinuous);

    this.startTime = 0;
    this.elapsedTime = 0;
    this.fireTickHandler({
      forceTransition: true,
    });
  }

  /**
   * Sets whether or not the timer will fire continuously, even when paused.
   *
   * @param {boolean} newIsContinuous
   */
  @action setIsContinuous(newIsContinuous) {
    this.isContinuous = Boolean(newIsContinuous);
  }

  /**
   * Updates the timer to a new time.
   *
   * @param {number} newTime The new time in milliseconds.
   */
  @action setTime(newTime) {
    const shouldResume = Boolean(!this.isPaused && this.startTime);
    this.pause();

    this.elapsedTime = Math.max(newTime, 0);
    this.fireTickHandler({
      isPaused: !shouldResume,
      forceTransition: true,
    });

    if (shouldResume) {
      this.start();
    }
  }

  /**
   * Sets the tick handler.
   *
   * @param {function} timeHandler
   */
  @action setTickHandler(timeHandler) {
    this.tickHandler = timeHandler;
  }

  /**
   * Sets the maximum amount of time (in milliseconds) that can elapse before the timer stops itself.
   *
   * @param {number} newMaxTime
   */
  @action setMaxTime(newMaxTime) {
    if (newMaxTime || newMaxTime === 0) {
      this.maxTime = newMaxTime;
    } else {
      this.maxTime = null;
    }
  }

  /**
   * Sets the fps for the game.
   *
   * @param {number} newFps
   */
  @action setFps(newFps) {
    const safeFps = parseInt(newFps, 10);

    const validFps = [60, 30, 20, 15, 12, 10]; // eslint-disable-line no-magic-numbers

    if (!safeFps) {
      throw new Error('Invalid fps given to gameTimer.setFps -- The value must a number.');
    } else if (validFps.indexOf(newFps) === -1) {
      throw new Error('Invalid fps given to gameTimer.setFps -- The value must be 60, 30, 20, 15, 12, 10.');
    }

    this.fps = safeFps;
  }
}

// Tells the system not to use this as an injectable store.
export const doNotInject = true;
